// daemon-settings.js — daemon-mode Settings page glue.
//
// The settings themselves are rendered by pages/settings-v2.js from the
// daemon's GET /v1/config/schema. This module keeps what other modules
// import: the "Saved" toast, the HMAC decrypt-status row, and the Team Sync
// panel (connection status + display name).
import { state } from "./state.js";
import { $ } from "./dom.js";
import { escapeHTML } from "./util.js";
import { api } from "./api.js";
import { renderSettingsV2 } from "./pages/settings-v2.js";

export function showSettingsToast() {
  const toast = $("settings-toast");
  if (!toast) return;
  toast.style.opacity = "1";
  setTimeout(() => {
    toast.style.opacity = "0";
  }, 1500);
}

// ---- HMAC decrypt-status row ----
//
// Paints the "Enabled / Disabled" badge on the Settings page next to
// the HMAC key input. Reads state.hmacKey at call time (so callers
// don't have to pass it). Also toggles the "Clear" button's
// visibility — there's nothing to clear when no key is loaded.
//
// Called from: the HMAC import flow in bootstrap.js (4 sites) +
// pages/settings.js's mount path.
export function updateDecryptStatus() {
  const el = $("settings-decrypt-status");
  if (!el) return;
  const importControls = $("hmac-import-controls");
  const clearRow = $("hmac-clear-row");
  if (state.hmacKey) {
    el.innerHTML =
      '<span style="color: var(--success); font-weight: 600;">Enabled</span> — file paths shown in real form.';
    // Key loaded: hide the import/paste affordances (nothing left to import) and
    // surface the Clear row so the key can still be removed.
    if (importControls) importControls.style.display = "none";
    if (clearRow) clearRow.style.display = "";
  } else {
    el.innerHTML =
      '<span style="color: var(--warning); font-weight: 600;">Disabled</span> — resources shown as hashed IDs.';
    if (importControls) importControls.style.display = "";
    if (clearRow) clearRow.style.display = "none";
  }
}

// ---- daemon settings page renderer ----

export function renderDaemonSettings() {
  renderSettingsV2();
  renderDaemonTeamPanel();
}

// renderDaemonTeamPanel paints the "Connected to team X as <role>"
// status row plus the display-name input. Hidden when the daemon
// isn't connected to a coordinator yet.
//
// PR #11+ bug-sweep rename: was renderTeamSettings(); collided with
// cloud's same-named function at line 5718 which renders a totally
// different DOM element (#team-settings-panel vs #settings-team-panel).
// Function-decl hoisting let the second definition shadow the first,
// so cloud's team-settings panel never re-rendered after PR #9 merge.
// Renamed here to disambiguate.
async function renderDaemonTeamPanel() {
  const panel = $("settings-team-panel");
  if (!panel) return;
  try {
    const info = await api("/v1/team/info");
    if (info.error === "not_connected") {
      panel.style.display = "";
      $("settings-team-status").textContent =
        "Not connected to a team. Run agc team create or agc team join.";
      $("settings-team-name-row").style.display = "none";
      return;
    }
    panel.style.display = "";
    $("settings-team-status").innerHTML =
      "Connected to <strong>" +
      escapeHTML(info.team_name) +
      "</strong> as " +
      info.your_role;
    // Show name editing
    const nameRow = $("settings-team-name-row");
    nameRow.style.display = "";
    // Find our display name from member list
    const members = await api("/v1/team/members").catch(() => ({ members: [] }));
    const me = (members.members || []).find((m) => m.is_you);
    if (me) {
      $("settings-team-display-name").value = me.display_name || "";
      $("settings-team-display-name").placeholder = me.laptop_id;
    }
  } catch {
    panel.style.display = "";
    $("settings-team-status").textContent = "Could not reach team coordinator.";
    $("settings-team-name-row").style.display = "none";
  }
}

// ---- module init ----
function wireListeners() {
  // Team settings: save display name. Daemon-mode panel only —
  // cloud mode uses a different display-name flow that lives in
  // pages/account.js.
  {
    const btn = $("settings-team-save-name");
    if (btn)
      btn.addEventListener("click", async () => {
        const name = ($("settings-team-display-name").value || "").trim();
        if (!name) return;
        try {
          const info = await api("/v1/team/info");
          if (info.error === "not_connected") return;
          await api(
            "/v1/team/members/" + encodeURIComponent(info.your_laptop_id) + "/name",
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ name }),
            },
          );
          showSettingsToast();
        } catch (err) {
          alert("Failed: " + (err.message || "unknown error"));
        }
      });
  }

}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", wireListeners);
} else {
  wireListeners();
}
